import React, { useState, useRef } from "react";
import "cropperjs/dist/cropper.css";
import CropperImage from "./CropperImage";
import { getCircularImage } from "./utils"; 

const ImagePhoto = ({ index, label, setImage, images, setImages, fileInputsRef }) => {
  // State to store the selected image and the cropped image URL
  const [selectedImage, setSelectedImage] = useState(null);
  // State to track if a file has been selected (for showing the delete button)
  const [fileSelected, setFileSelected] = useState(false);
  // State for error messages (invalid file type or oversized image)
  const [error, setError] = useState("");
  // State for oversized file detection
  const [oversizedFile, setOversizedFile] = useState(false);
  // Ref for accessing the CropperImage component
  const cropperRef = useRef(null);

  // Handler for image file upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];
      const maxSizeMB = 2;

      // File type validation
      if (!allowedFormats.includes(file.type)) {
        setError("Only JPG, JPEG, and PNG files are allowed.");
        setOversizedFile(false);
        return;
      }

      // File size validation
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError("Photo size up to 2MB only.");
        setOversizedFile(true);
        return;
      }

      // Clear any previous errors
      setError("");
      setOversizedFile(false);
      setFileSelected(true); // Mark file as selected
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target.result); // Set image preview for cropping
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler for applying the crop to the image
  const handleCrop = async () => {
  if (cropperRef.current) {
    const croppedDataUrl = await cropperRef.current.getCroppedImage();

    // ⬇️ Apply circular transformation to the cropped image
    const circularPhoto = await getCircularImage(croppedDataUrl);

    setImages((prev) => {
      const updated = [...prev];
      updated[index] = circularPhoto; // Save the circular image instead
      return updated;
    });

    setImage(circularPhoto); // Set the circular image for parent
    setSelectedImage(null); // Reset preview
    setFileSelected(false);
    setError("");
  }
};

  // Handler for canceling the crop (resets preview)
  const handleCancelCrop = () => {
    setSelectedImage(null); // Reset the preview image after canceling
    // Don't reset fileSelected here — we still want to show the delete button
    setError(""); // Clear any errors
  };

  // Handler for deleting the image (reset states)
  const handleDelete = () => {
    setImages((prev) => {
      const updated = [...prev];
      updated[index] = null; // Set the image to null in the images array
      return updated;
    });
    if (fileInputsRef.current[index]) {
      fileInputsRef.current[index].value = ""; // Clear the input field
    }
    setSelectedImage(null); // Reset selected image preview
    setError(""); // Clear any errors
    setOversizedFile(false); // Reset oversized file state
    setFileSelected(false); // Reset file selected state
  };

  return (
    <div
      style={{
        border: "2px solid #ddd",
        borderRadius: "8px",
        padding: "15px",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "20px",
        position: "relative",
      }}
    >
      <div style={{ position: "relative" }}>
        <label style={{ color: "#3f51b5", fontWeight: "bold" }}>
          {label}:
          <input
            ref={(el) => (fileInputsRef.current[index] = el)}
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handleImageUpload} // Trigger the image upload handler
            style={{
              display: images[index] ? "none" : "block",
              marginTop: "10px",
              padding: "5px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          />
        </label>

        {/* Show the delete button if a file is selected (oversized or regular) */}
        {(oversizedFile || fileSelected) && !images[index] && (
          <button
            onClick={handleDelete} // Trigger delete handler
            style={{
              position: "absolute",
              top: "12px",
              right: "-8px",
              backgroundColor: "red",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "18px",
              height: "18px",
              cursor: "pointer",
              fontSize: "12px",
              lineHeight: "18px",
              padding: "0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            X
          </button>
        )}

        {/* Show cropper if an image is selected for cropping */}
        {selectedImage && (
          <div
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              width: "70%",
              height: "70%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <div style={{ position: "relative", textAlign: "center", backgroundColor: "#fff", padding: "15px", borderRadius: "10px" }}>
              <CropperImage image={selectedImage} ref={cropperRef} />

              <div style={{ marginTop: "10px", display: "flex", gap: "10px", justifyContent: "center" }}>
                <button
                  onClick={handleCrop} // Trigger crop handler
                  style={{
                    backgroundColor: "#4CAF50",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Apply Crop
                </button>
                <button
                  onClick={handleCancelCrop} // Trigger cancel crop handler
                  style={{
                    backgroundColor: "#f44336",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Display cropped image if available */}
      {images[index] && (
        <div style={{ marginTop: "10px", position: "relative" }}>
          <img
            src={images[index]}
            alt={`Uploaded ${index}`}
            style={{
              width: "100px",
              height: "100px",
              objectFit: "cover",
              border: "1px solid #ddd",
              borderRadius: "50%",
              display: "block",
              margin: "0 auto",
            }}
          />
          <button
            onClick={handleDelete} // Trigger delete handler
            style={{
              position: "absolute",
              top: "-5px",
              right: "-5px",
              backgroundColor: "red",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            X
          </button>
        </div>
      )}

      {/* Show error message if there's any validation error */}
      {error && (
        <div
          style={{
            position: "absolute",
            bottom: "0px",
            left: "0",
            color: "red",
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ImagePhoto;
